import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useViewerRoles, useWorkspace } from '@treams/treams-hooks';
import {
	DismissFeatureType,
	useDismissFeatures,
} from '@treams/treams-hooks/src/useDismissFeatures';
import { CloseIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { useWorkspaceFeaturesQuery } from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	FlexGap,
	GraphQLErrorMessage,
	IconWrapper,
	Paragraph,
	Subheading,
} from '@treams/treams-ui';

export const FeatureAnnouncement = ({
	feature,
	onEnableSuccess,
}: {
	feature: graphql.WorkspaceFeatureFragment;
	onEnableSuccess: () => void;
}) => {
	const { isWorkspaceSuperuser, isAdministrator } = useViewerRoles();

	const { dismissFeature } = useDismissFeatures();

	const { t } = useTranslation();

	const [updateWorkspaceFeature, { loading: updatingFeature }] =
		graphql.useUpdateWorkspaceFeatureMutation();
	const [approve, { loading: approving }] = graphql.useApproveFeatureForWorkspaceMutation();

	const featureApproved = Boolean(feature.workspaceApproval?.approvedAt);
	const [featureDismissed, setFeatureDismissed] = useState<graphql.FeatureName | null>(null);

	const [error, attempt] = useErrorBoundary();

	if (feature?.feature?.id === 'CONVERSATION_AI_INSIGHTS') return null;

	if (!isWorkspaceSuperuser && !isAdministrator) {
		return null;
	}

	if (!feature.requiredConditions) {
		return null;
	}

	if (featureDismissed) {
		return null;
	}

	const enableFeature = attempt(async () => {
		const result = await updateWorkspaceFeature({
			variables: {
				input: { featureID: feature.feature.id, status: graphql.WorkspaceFeatureStatus.Enabled },
			},
		});

		if (result.data) {
			onEnableSuccess();
		}
	});

	const dismissFeatureAnnouncement = () => {
		dismissFeature(DismissFeatureType.FEATURE_ANNOUNCEMENT, feature.feature.id);

		setFeatureDismissed(feature.feature.id);
	};

	if (featureApproved) {
		return (
			<>
				<GraphQLErrorMessage error={error} />
				<Callout color="blue" width="100%" maxWidth={1000}>
					<FlexGap gap={8} flexDirection="column" width="100%">
						<FlexGap gap={2} justifyContent="space-between">
							<Paragraph>
								{!feature.workspaceApproval?.approvedAt &&
									t('settings.organization.settings.featureAnnouncements.needsApproval.success')}
								<Trans
									i18nKey="settings.organization.settings.featureAnnouncements.enable.title"
									values={{
										featureName: t(
											`settings.organization.settings.${feature.feature.id.replace(/\./g, '_')}`,
										),
									}}
									components={{
										featureName: <strong />,
									}}
								/>
							</Paragraph>

							<IconWrapper onClick={dismissFeatureAnnouncement} style={{ cursor: 'pointer' }}>
								<CloseIcon />
							</IconWrapper>
						</FlexGap>

						<FlexGap gap={10} alignItems="center">
							<Button
								title={t('settings.organization.settings.featureAnnouncements.enable.button')}
								onClick={enableFeature}
								type="secondary"
								small
								loading={updatingFeature}
							/>
						</FlexGap>
					</FlexGap>
				</Callout>
			</>
		);
	}

	const approveAndEnableFeature = attempt(async () => {
		if (!feature.requiredConditions) {
			throw new Error('No required conditions for feature found');
		}

		const approvalResult = await approve({
			variables: {
				input: {
					featureID: feature.feature.id,
					conditionsVersion: feature.requiredConditions.currentVersion,
				},
			},
		});

		if (!approvalResult.data?.approveFeatureForWorkspace.approval) {
			throw new Error('Something went wrong while approving the feature');
		}

		const updateResult = await updateWorkspaceFeature({
			variables: {
				input: { featureID: feature.feature.id, status: graphql.WorkspaceFeatureStatus.Enabled },
			},
		});

		if (updateResult.data) {
			onEnableSuccess();
		}
	});

	return (
		<Callout color="blue" width="100%" maxWidth={1000}>
			<Box>
				<GraphQLErrorMessage error={error} />
				<FlexGap gap={8} justifyContent="space-between">
					<Subheading>
						{t('settings.organization.settings.featureAnnouncements.title')}{' '}
						{t(`settings.organization.settings.${feature.feature.id.replace(/\./g, '_')}`)}
					</Subheading>
					<IconWrapper onClick={dismissFeatureAnnouncement} style={{ cursor: 'pointer' }}>
						<CloseIcon />
					</IconWrapper>
				</FlexGap>
				<Paragraph>
					{t('settings.organization.settings.needsApproval.description')}{' '}
					<a
						href={feature.requiredConditions.linkToConditions}
						target="_blank"
						rel="noreferrer"
						style={{ textDecoration: 'underline' }}
					>
						{t('settings.organization.settings.needsApproval.link')}
					</a>
				</Paragraph>
				<Box marginTop={3} maxWidth={200}>
					<Button
						type="secondary"
						title={t('settings.organization.settings.needsApproval.accept')}
						small
						loading={approving || updatingFeature}
						onClick={approveAndEnableFeature}
					/>
				</Box>
			</Box>
		</Callout>
	);
};

export const FeatureAnnouncements = () => {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const { isWorkspaceSuperuser, isAdministrator } = useViewerRoles();

	const { isFeatureDismissed } = useDismissFeatures();

	const [featureEnabled, setFeatureEnabled] = useState<boolean>(false);

	const { data } = useWorkspaceFeaturesQuery({
		variables: {
			id: workspace.id,
		},
		skip: !isWorkspaceSuperuser && !isAdministrator,
	});

	const featureAnnouncements = data?.workspace.features.filter((feature) => {
		if (feature.feature.stability === graphql.FeatureStability.Alpha) {
			return false;
		}

		if (!feature.requiredConditions) {
			return false;
		}

		const featureHasApproval = feature.workspaceApproval?.approvedAt;
		const featureIsDisabled = feature.status !== graphql.WorkspaceFeatureStatus.Enabled;

		if (featureHasApproval && !featureIsDisabled) {
			return false;
		}

		if (isFeatureDismissed(DismissFeatureType.FEATURE_ANNOUNCEMENT, feature.feature.id)) {
			return false;
		}

		return feature.needsWorkspaceApproval || (featureHasApproval && featureIsDisabled);
	});

	if (featureEnabled) {
		return (
			<Container pb={3} wide={1000}>
				<Callout color="green" width="100%">
					<Paragraph>
						{t('settings.organization.settings.featureAnnouncements.enable.success')}
					</Paragraph>
				</Callout>
			</Container>
		);
	}

	if (!featureAnnouncements || featureAnnouncements.length === 0) {
		return null;
	}

	// We show the first feature announcement that needs approval
	const feature = featureAnnouncements[0];

	// Temporary feature announcement block for these workspaces
	const blockedWorkspaces = ['schiphol', 'schuiteman', 'wehkamp'];

	if (blockedWorkspaces.includes(workspace.id)) {
		return null;
	}

	return (
		<Container pb={3} mt={-3} wide>
			<Flex justifyContent="center">
				<FeatureAnnouncement feature={feature} onEnableSuccess={() => setFeatureEnabled(true)} />
			</Flex>
		</Container>
	);
};
